import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import StylePicker from "../components/video/StylePicker";
import VoiceSampleButton from "../components/video/VoiceSampleButton";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layers, Plus, Loader2, Sparkles, Trash2, Repeat, Zap, Clock, CalendarClock, AlertTriangle, CheckCircle2, Flame, Play, Square, RefreshCw, Mic } from "lucide-react";
import MusicPicker from "@/components/MusicPicker";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const NICHES = ["Finance & Money", "Tech & AI", "History", "Motivation", "True Crime", "Health & Fitness", "Science", "Business", "Gaming", "General"];
const TONES = ["Energetic & engaging", "Calm & documentary", "Bold & provocative", "Educational & clear", "Storytelling & cinematic"];
const CADENCE = ["Daily", "Weekly", "Bi-weekly", "Monthly"];
const PLATFORMS = ["YouTube", "TikTok", "Instagram"];
const CADENCE_LABEL = { daily: "Daily", weekly: "Weekly", biweekly: "Bi-weekly", monthly: "Monthly" };
const FREQ_OPTS = [
  { v: "daily", l: "Every day" },
  { v: "every2", l: "Every 2 days" },
  { v: "every3", l: "Every 3 days" },
  { v: "weekly", l: "Weekly" },
];
const COST_SCRIPT = 10, COST_VO_OPENAI = 5, COST_VO_ELEVENLABS = 10, COST_IMAGE = 5, SCENES_PER_EPISODE = 7;
const voCost = (voiceId) => (voiceId?.startsWith("openai:") || voiceId?.startsWith("narrator:") ? COST_VO_OPENAI : COST_VO_ELEVENLABS);
const episodeCost = (withVisuals, voiceId) => COST_SCRIPT + voCost(voiceId) + (withVisuals ? COST_IMAGE * SCENES_PER_EPISODE : 0);
const fmtRun = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
};
const relTime = (iso) => {
  if (!iso) return "";
  const diff = new Date(iso).getTime() - Date.now();
  if (diff <= 0) return "now";
  const m = Math.round(diff / 60000);
  if (m < 60) return `in ${m}m`;
  const h = Math.round(m / 60);
  if (h < 48) return `in ${h}h`;
  return `in ${Math.round(h / 24)}d`;
};

export default function Series() {
  const [series, setSeries] = useState([]);
  const [limit, setLimit] = useState(null);
  const [open, setOpen] = useState(false);
  const [genLoading, setGenLoading] = useState(null);
  const [previewing, setPreviewing] = useState(null);
  const previewRef = useRef(null);

  const playPreview = async (sid) => {
    if (previewing === sid) {
      previewRef.current?.pause();
      setPreviewing(null);
      return;
    }
    previewRef.current?.pause();
    setPreviewing(`loading:${sid}`);
    try {
      const resp = await api.get(`/series/${sid}/sound-preview`, { responseType: "blob" });
      const a = new Audio(URL.createObjectURL(resp.data));
      a.onended = () => setPreviewing(null);
      previewRef.current = a;
      await a.play();
      setPreviewing(sid);
    } catch (err) {
      toast.error(apiError(err));
      setPreviewing(null);
    }
  };
  const [apFor, setApFor] = useState(null);
  const [batchFor, setBatchFor] = useState(null);
  const [voices, setVoices] = useState([]);
  const [streak, setStreak] = useState(null);
  const { user, updateCredits } = useAuth();
  const navigate = useNavigate();

  const load = () => api.get("/series").then((r) => setSeries(r.data));

  const [finishingBatch, setFinishingBatch] = useState(null);
  const finishBatch = async (sid) => {
    setFinishingBatch(sid);
    try {
      const { data } = await api.post(`/series/${sid}/resume-batch`);
      toast.success(`Finishing the batch — ${data.remaining} episode${data.remaining === 1 ? "" : "s"} to go.`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setFinishingBatch(null);
    }
  };
  const loadStats = () => api.get("/stats").then((r) => setLimit(r.data.series_limit)).catch(() => {});
  const loadStreak = () => api.get("/streak").then((r) => setStreak(r.data)).catch(() => {});
  useEffect(() => {
    load();
    loadStats();
    loadStreak();
    api.get("/voices").then((r) => setVoices(r.data.voices || [])).catch(() => {});
  }, []);

  // Poll while any series is mid-batch so progress badges update live.
  useEffect(() => {
    const anyRunning = series.some((s) => s.batch_status === "running");
    if (!anyRunning) return;
    const t = setInterval(() => { load(); }, 6000);
    return () => clearInterval(t);
  }, [series]);

  // Tick every 60s so the "next post in Xh" countdown stays fresh.
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 60000);
    return () => clearInterval(t);
  }, []);

  const atLimit = limit != null && series.length >= limit;

  const generateEpisode = async (sid) => {
    setGenLoading(sid);
    try {
      const { data } = await api.post(`/series/${sid}/generate-episode`);
      updateCredits(data.credits);
      toast.success("Episode generated!");
      navigate(`/video/${data.video.id}`);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setGenLoading(null);
    }
  };

  const remove = async (sid) => {
    await api.delete(`/series/${sid}`);
    load();
    toast.success("Series deleted");
  };

  return (
    <div className="space-y-6" data-testid="series-page">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">Recurring content</p>
          <h1 className="font-head text-3xl font-bold tracking-tight">Series</h1>
          <p className="text-zinc-400 mt-1 text-sm">
            Consistency grows channels. Spin up a series and forge episodes on demand.
            {limit != null && (
              <span className="ml-1 text-zinc-500" data-testid="series-usage">· {series.length} / {limit} series used</span>
            )}
          </p>
        </div>
        <button onClick={() => atLimit ? navigate("/credits") : setOpen(true)} data-testid="new-series-btn" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 px-4 py-2 rounded-md text-sm font-semibold transition-colors">
          <Plus className="h-4 w-4" /> {atLimit ? "Upgrade for more" : "New series"}
        </button>
      </div>

      <StreakMeter data={streak} />

      {series.length === 0 ? (
        <div className="border border-dashed border-white/10 rounded-md p-16 text-center">
          <Layers className="h-10 w-10 text-zinc-600 mx-auto mb-4" />
          <p className="text-zinc-400">No series yet. Create one to batch-produce content.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {series.map((s) => (
            <div key={s.id} className="border border-white/10 rounded-md bg-[#121212] p-5" data-testid={`series-${s.id}`}>
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <h3 className="font-head text-lg font-semibold truncate">{s.name}</h3>
                  <p className="text-sm text-zinc-500 truncate">{s.topic}</p>
                </div>
                <button onClick={() => remove(s.id)} className="text-zinc-500 hover:text-red-400 transition-colors shrink-0 ml-2"><Trash2 className="h-4 w-4" /></button>
              </div>
              <div className="flex flex-wrap gap-2 mt-3 text-xs">
                <Tag>{s.niche}</Tag>
                <Tag><Repeat className="h-3 w-3" /> {s.cadence}</Tag>
                <Tag>{s.platform}</Tag>
                <Tag>{s.episodes} episodes</Tag>
              </div>
              <div className="flex items-center gap-4 mt-3 text-xs text-zinc-400" data-testid={`series-analytics-${s.id}`}>
                <span className="inline-flex items-center gap-1.5" data-testid={`series-scheduled-${s.id}`}><CalendarClock className="h-3.5 w-3.5 text-sky-400" /> {s.posts_scheduled || 0} scheduled</span>
                <span className="inline-flex items-center gap-1.5" data-testid={`series-published-${s.id}`}><CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" /> {s.posts_published || 0} published</span>
                {s.next_post_at && (
                  <span className="inline-flex items-center gap-1.5 ml-auto text-sky-300" data-testid={`series-next-${s.id}`}><Clock className="h-3.5 w-3.5" /> next {relTime(s.next_post_at)}</span>
                )}
              </div>
              {s.autopilot && (
                <div className="mt-3 flex items-center gap-1.5 text-xs text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-md px-2.5 py-1.5" data-testid={`autopilot-status-${s.id}`}>
                  <Zap className="h-3.5 w-3.5" />
                  <span>Autopilot on · {CADENCE_LABEL[s.autopilot_cadence] || "Weekly"}</span>
                  <span className="text-emerald-300/60 inline-flex items-center gap-1 ml-auto"><Clock className="h-3 w-3" /> next {fmtRun(s.autopilot_next_run)}</span>
                </div>
              )}
              {s.batch_status && !["done", "failed"].includes(s.batch_status) && (
                <div className={`mt-3 flex items-center gap-1.5 text-xs rounded-md px-2.5 py-1.5 border ${s.batch_status === "paused_low_credits" ? "text-amber-300 bg-amber-500/10 border-amber-500/20" : "text-sky-300 bg-sky-500/10 border-sky-500/20"}`} data-testid={`batch-status-${s.id}`}>
                  {s.batch_status === "running" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <AlertTriangle className="h-3.5 w-3.5" />}
                  <span>
                    {s.batch_status === "paused_low_credits"
                      ? `Batch paused — ${s.batch_done || 0}/${s.batch_total || 0} scheduled (low credits)`
                      : `Scheduling ${s.batch_done || 0}/${s.batch_total || 0} episodes…`}
                  </span>
                  {s.batch_status === "running" && s.batch_resumed_at && (
                    <span className="text-[10px] text-amber-300/90 font-semibold" data-testid={`batch-resumed-${s.id}`}>auto-resumed</span>
                  )}
                  {s.batch_next_run && <span className="inline-flex items-center gap-1 ml-auto opacity-70"><Clock className="h-3 w-3" /> {fmtRun(s.batch_next_run)}</span>}
                </div>
              )}
              {s.batch_status === "failed" && (
                <div className="mt-3 flex items-center gap-1.5 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded-md px-2.5 py-1.5" data-testid={`batch-status-${s.id}`}>
                  <AlertTriangle className="h-3.5 w-3.5" />
                  <span>Batch stopped at {s.batch_done || 0}/{s.batch_total || 0} — episodes already made are safe. Start a new batch to finish.</span>
                </div>
              )}
              {["failed", "paused_low_credits"].includes(s.batch_status) && (s.batch_done || 0) < (s.batch_total || 0) && (
                <button
                  onClick={() => finishBatch(s.id)}
                  disabled={finishingBatch === s.id}
                  data-testid={`finish-batch-${s.id}`}
                  className="mt-2 w-full inline-flex items-center justify-center gap-2 bg-amber-500/15 border border-amber-500/30 hover:bg-amber-500/25 text-amber-300 py-2 rounded-md text-xs font-semibold transition-colors disabled:opacity-60"
                >
                  {finishingBatch === s.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                  Finish this batch ({(s.batch_total || 0) - (s.batch_done || 0)} episode{(s.batch_total || 0) - (s.batch_done || 0) === 1 ? "" : "s"} left)
                </button>
              )}
              {s.batch_status === "done" && (
                <div className="mt-3 flex items-center gap-1.5 text-xs text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-md px-2.5 py-1.5" data-testid={`batch-status-${s.id}`}>
                  <CalendarClock className="h-3.5 w-3.5" /> <span>{s.batch_total || 0} episode{(s.batch_total || 0) === 1 ? "" : "s"} scheduled</span>
                </div>
              )}
              <div className="grid grid-cols-3 gap-2 mt-4">
                <button onClick={() => generateEpisode(s.id)} disabled={genLoading === s.id} data-testid={`generate-episode-${s.id}`} className="inline-flex items-center justify-center gap-2 bg-white/5 border border-white/10 hover:bg-white/10 py-2.5 rounded-md text-sm font-semibold transition-colors disabled:opacity-60">
                  {genLoading === s.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  <span className="truncate">Ep {s.episodes + 1}</span>
                </button>
                <button onClick={() => setBatchFor(s)} data-testid={`schedule-batch-btn-${s.id}`} className="inline-flex items-center justify-center gap-2 py-2.5 rounded-md text-sm font-semibold transition-colors border bg-sky-600/15 border-sky-500/40 text-sky-300 hover:bg-sky-600/25">
                  <CalendarClock className="h-4 w-4" /> Schedule
                </button>
                <button onClick={() => setApFor(s)} data-testid={`autopilot-btn-${s.id}`} className={`inline-flex items-center justify-center gap-2 py-2.5 rounded-md text-sm font-semibold transition-colors border ${s.autopilot ? "bg-emerald-600/15 border-emerald-500/40 text-emerald-300 hover:bg-emerald-600/25" : "bg-white/5 border-white/10 hover:bg-white/10"}`}>
                  <Zap className="h-4 w-4" /> {s.autopilot ? "On" : "Auto"}
                </button>
              </div>
              <SeriesNarratorRow s={s} voices={voices} onSaved={load} />
              <div className="mt-3 flex items-center gap-2" data-testid={`series-music-${s.id}`}>
                <div className="flex-1 min-w-0">
                  <MusicPicker
                    track={s.music_track}
                    volume={s.music_volume}
                    testid={`series-music-select-${s.id}`}
                    onChange={async (v) => {
                      try {
                        await api.put(`/series/${s.id}/music`, v);
                        toast.success(v.track ? "New episodes will include this soundtrack." : "Music removed for new episodes.");
                        load();
                      } catch (err) {
                        toast.error(apiError(err));
                      }
                    }}
                  />
                </div>
                <button
                  onClick={() => playPreview(s.id)}
                  disabled={previewing === `loading:${s.id}`}
                  data-testid={`series-sound-preview-${s.id}`}
                  title="Hear a 10s sample of how episodes will sound (voice + music)"
                  className="shrink-0 h-9 w-9 flex items-center justify-center rounded-sm border border-white/10 bg-[#0A0A0A] text-zinc-300 hover:text-white hover:bg-white/5 transition-colors disabled:opacity-60"
                >
                  {previewing === `loading:${s.id}` ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : previewing === s.id ? (
                    <Square className="h-4 w-4" />
                  ) : (
                    <Play className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <NewSeriesDialog open={open} voices={voices} onClose={() => setOpen(false)} onCreated={() => { setOpen(false); load(); }} />
      <AutopilotDialog series={apFor} onClose={() => setApFor(null)} onSaved={() => { setApFor(null); load(); }} />
      <BatchScheduleDialog series={batchFor} voices={voices} credits={user?.credits ?? 0} onClose={() => setBatchFor(null)} onStarted={() => { setBatchFor(null); load(); }} />
    </div>
  );
}

function AutopilotDialog({ series, onClose, onSaved }) {
  const open = !!series;
  const [enabled, setEnabled] = useState(false);
  const [cadence, setCadence] = useState("weekly");
  const [time, setTime] = useState("09:00");
  const [platforms, setPlatforms] = useState(["YouTube"]);
  const [privacy, setPrivacy] = useState("public");
  const [genImages, setGenImages] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (series) {
      setEnabled(!!series.autopilot);
      setCadence(series.autopilot_cadence || "weekly");
      setTime(series.autopilot_time || "09:00");
      setPlatforms(series.autopilot_platforms || [series.platform === "TikTok" ? "TikTok" : "YouTube"]);
      setPrivacy(series.autopilot_privacy || "public");
      setGenImages(!!series.autopilot_generate_images);
    }
  }, [series]);

  const togglePlatform = (p) => setPlatforms((cur) => (cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p]));

  const save = async () => {
    if (enabled && platforms.length === 0) { toast.error("Pick at least one platform"); return; }
    setSaving(true);
    try {
      await api.put(`/series/${series.id}/autopilot`, { enabled, cadence, time, platforms, privacy, generate_images: genImages });
      toast.success(enabled ? "Autopilot on — episodes will publish themselves 🚀" : "Autopilot turned off");
      onSaved();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  let saveLabel = "Save";
  if (saving) saveLabel = "Saving…";
  else if (enabled) saveLabel = "Turn on autopilot";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#121212] border-white/10 text-white" data-testid="autopilot-dialog">
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2"><Zap className="h-4 w-4 text-emerald-400" /> Series autopilot</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-zinc-400 -mt-1">Auto-generate a new episode (script + voiceover) and publish it on a schedule — fully hands-off.</p>

          <button
            onClick={() => setEnabled((v) => !v)}
            data-testid="autopilot-toggle"
            className={`w-full flex items-center justify-between px-4 py-3 rounded-md border transition-colors ${enabled ? "bg-emerald-600/15 border-emerald-500/40" : "bg-[#0A0A0A] border-white/10"}`}
          >
            <span className="text-sm font-semibold inline-flex items-center gap-2"><Zap className={`h-4 w-4 ${enabled ? "text-emerald-400" : "text-zinc-500"}`} /> {enabled ? "Autopilot enabled" : "Autopilot disabled"}</span>
            <span className={`relative inline-flex h-5 w-9 rounded-full transition-colors ${enabled ? "bg-emerald-500" : "bg-zinc-700"}`}>
              <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${enabled ? "left-4.5" : "left-0.5"}`} style={{ left: enabled ? "1.125rem" : "0.125rem" }} />
            </span>
          </button>

          <div className={`space-y-4 transition-opacity ${enabled ? "opacity-100" : "opacity-40 pointer-events-none"}`}>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs uppercase tracking-[0.15em] text-zinc-500 mb-1.5">Cadence</label>
                <select value={cadence} onChange={(e) => setCadence(e.target.value)} data-testid="autopilot-cadence" className="w-full bg-[#0A0A0A] border border-white/10 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 [color-scheme:dark]">
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Bi-weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.15em] text-zinc-500 mb-1.5">Time (UTC)</label>
                <input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="autopilot-time" className="w-full bg-[#0A0A0A] border border-white/10 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 [color-scheme:dark]" />
              </div>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-[0.15em] text-zinc-500 mb-1.5">Publish to</label>
              <div className="grid grid-cols-2 gap-2">
                {["YouTube", "TikTok"].map((p) => (
                  <button key={p} onClick={() => togglePlatform(p)} data-testid={`autopilot-platform-${p}`} className={`py-2.5 rounded-md border text-sm font-medium transition-colors ${platforms.includes(p) ? "border-emerald-500 bg-emerald-600/15 text-white" : "border-white/10 text-zinc-500 hover:text-white"}`}>{p}</button>
                ))}
              </div>
              {platforms.includes("YouTube") && (
                <p className="text-[11px] text-amber-400/80 mt-1.5">YouTube must be connected in Settings, or auto-posts will fail.</p>
              )}
            </div>

            {platforms.includes("YouTube") && (
              <div>
                <label className="block text-xs uppercase tracking-[0.15em] text-zinc-500 mb-1.5">YouTube privacy</label>
                <select value={privacy} onChange={(e) => setPrivacy(e.target.value)} data-testid="autopilot-privacy" className="w-full bg-[#0A0A0A] border border-white/10 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 [color-scheme:dark]">
                  <option value="public">Public</option>
                  <option value="unlisted">Unlisted</option>
                  <option value="private">Private</option>
                </select>
              </div>
            )}

            <button onClick={() => setGenImages((v) => !v)} data-testid="autopilot-genimages" className="w-full flex items-center justify-between px-4 py-3 rounded-md border border-white/10 bg-[#0A0A0A]">
              <span className="text-sm">Auto-generate scene visuals</span>
              <span className={`relative inline-flex h-5 w-9 rounded-full transition-colors ${genImages ? "bg-blue-500" : "bg-zinc-700"}`}>
                <span className="absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all" style={{ left: genImages ? "1.125rem" : "0.125rem" }} />
              </span>
            </button>
            <p className="text-[11px] text-zinc-500">Autopilot pauses automatically if your credits run low.</p>
          </div>

          <button onClick={save} disabled={saving} data-testid="autopilot-save-btn" className="w-full bg-emerald-600 hover:bg-emerald-500 py-2.5 rounded-md font-semibold transition-colors disabled:opacity-60">
            {saveLabel}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function VoiceSelect({ voices, value, onChange, testid }) {
  // Series voices power background episodes (autopilot/batch) — included voices only
  // (AI narrators + OpenAI), so scheduled videos never spend ElevenLabs credits.
  const narrators = voices.filter((v) => v.voice_id.startsWith("narrator:"));
  const openai = voices.filter((v) => v.voice_id.startsWith("openai:"));
  const first = narrators[0]?.voice_id || openai[0]?.voice_id || "";
  const safeValue = value?.startsWith("openai:") || value?.startsWith("narrator:") ? value : first;
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.15em] text-zinc-500 mb-1.5">Narrator voice</label>
      <div className="flex gap-2">
        <select value={safeValue} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="flex-1 min-w-0 bg-[#0A0A0A] border border-white/10 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 [color-scheme:dark]">
          {narrators.length + openai.length === 0 && <option value="">Loading voices…</option>}
          {narrators.length > 0 && <optgroup label="AI narrators — consistent persona (5 credits)">{narrators.map((v) => <option key={v.voice_id} value={v.voice_id}>{v.name}{v.labels?.style ? ` · ${v.labels.style}` : ""}</option>)}</optgroup>}
          {openai.length > 0 && <optgroup label="Included voices — no ElevenLabs credits (5 credits)">{openai.map((v) => <option key={v.voice_id} value={v.voice_id}>{v.name}{v.labels?.gender ? ` · ${v.labels.gender}` : ""}</option>)}</optgroup>}
        </select>
        <VoiceSampleButton voices={voices} voiceId={safeValue} testid={`${testid}-sample`} />
      </div>
    </div>
  );
}

function Toggle({ label, checked, onChange, testid, color = "blue" }) {
  const on = color === "emerald" ? "bg-emerald-500" : color === "sky" ? "bg-sky-500" : "bg-blue-500";
  return (
    <button type="button" onClick={() => onChange(!checked)} data-testid={testid} className="w-full flex items-center justify-between px-4 py-3 rounded-md border border-white/10 bg-[#0A0A0A]">
      <span className="text-sm">{label}</span>
      <span className={`relative inline-flex h-5 w-9 rounded-full transition-colors ${checked ? on : "bg-zinc-700"}`}>
        <span className="absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all" style={{ left: checked ? "1.125rem" : "0.125rem" }} />
      </span>
    </button>
  );
}

function BatchScheduleDialog({ series, voices, credits, onClose, onStarted }) {
  const open = !!series;
  const [count, setCount] = useState(5);
  const [frequency, setFrequency] = useState("daily");
  const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState(tomorrow);
  const [time, setTime] = useState("15:00");
  const [voiceId, setVoiceId] = useState("");
  const [genImages, setGenImages] = useState(true);
  const [autoPublish, setAutoPublish] = useState(true);
  const [platforms, setPlatforms] = useState(["YouTube"]);
  const [privacy, setPrivacy] = useState("public");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (series) {
      setCount(5); setFrequency("daily"); setStartDate(tomorrow); setTime("15:00");
      setVoiceId(series.voice_id?.startsWith("openai:") || series.voice_id?.startsWith("narrator:") ? series.voice_id : "openai:alloy");
      setGenImages(series.generate_images !== false);
      setAutoPublish(true);
      setPlatforms([series.platform === "TikTok" ? "TikTok" : "YouTube"]);
      setPrivacy("public");
    }
  }, [series]); // eslint-disable-line

  const perVideo = episodeCost(genImages, voiceId);
  const total = perVideo * Math.max(1, count);
  const enough = credits >= total;
  const affordable = Math.floor(credits / perVideo);
  const togglePlatform = (p) => setPlatforms((cur) => (cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p]));

  const start = async () => {
    if (count < 1) { toast.error("Choose at least 1 video"); return; }
    if (autoPublish && platforms.length === 0) { toast.error("Pick at least one platform"); return; }
    if (credits < perVideo) { toast.error("Not enough credits — please refill to schedule this series."); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/series/${series.id}/schedule-batch`, {
        count: Number(count), frequency, start_date: startDate, time,
        voice_id: voiceId, generate_images: genImages, auto_publish: autoPublish, platforms, privacy,
      });
      toast.success(`Scheduling ${data.count} episodes — they'll ${autoPublish ? "auto-publish" : "be queued"} at your chosen times 🚀`);
      onStarted();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#121212] border-white/10 text-white max-h-[88vh] overflow-y-auto" data-testid="batch-schedule-dialog">
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2"><CalendarClock className="h-4 w-4 text-sky-400" /> Schedule a batch of episodes</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-zinc-400 -mt-1">Generate original episodes for <strong className="text-zinc-200">{series?.name}</strong> and auto-publish them on a schedule.</p>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-[0.15em] text-zinc-500 mb-1.5">Number of videos</label>
              <input type="number" min={1} max={20} value={count} onChange={(e) => setCount(Math.max(1, Math.min(20, Number(e.target.value) || 1)))} data-testid="batch-count" className="w-full bg-[#0A0A0A] border border-white/10 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 [color-scheme:dark]" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.15em] text-zinc-500 mb-1.5">Post every</label>
              <select value={frequency} onChange={(e) => setFrequency(e.target.value)} data-testid="batch-frequency" className="w-full bg-[#0A0A0A] border border-white/10 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 [color-scheme:dark]">
                {FREQ_OPTS.map((f) => <option key={f.v} value={f.v}>{f.l}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-[0.15em] text-zinc-500 mb-1.5">Start date</label>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="batch-start-date" className="w-full bg-[#0A0A0A] border border-white/10 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 [color-scheme:dark]" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.15em] text-zinc-500 mb-1.5">Time (UTC)</label>
              <input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="batch-time" className="w-full bg-[#0A0A0A] border border-white/10 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 [color-scheme:dark]" />
            </div>
          </div>
          <p className="text-[11px] text-sky-300/70 -mt-2 inline-flex items-center gap-1"><Sparkles className="h-3 w-3" /> Best-time tip: early afternoon UTC (13:00–16:00) tends to hit US/EU evening peaks — great for SEO reach.</p>

          <VoiceSelect voices={voices} value={voiceId} onChange={setVoiceId} testid="batch-voice" />

          <Toggle label="Auto-generate scene visuals" checked={genImages} onChange={setGenImages} testid="batch-genimages" />

          <Toggle label="Auto-publish at the scheduled time" checked={autoPublish} onChange={setAutoPublish} testid="batch-autopublish" color="sky" />

          {autoPublish && (
            <div>
              <label className="block text-xs uppercase tracking-[0.15em] text-zinc-500 mb-1.5">Publish to</label>
              <div className="grid grid-cols-2 gap-2">
                {["YouTube", "TikTok"].map((p) => (
                  <button key={p} onClick={() => togglePlatform(p)} data-testid={`batch-platform-${p}`} className={`py-2.5 rounded-md border text-sm font-medium transition-colors ${platforms.includes(p) ? "border-sky-500 bg-sky-600/15 text-white" : "border-white/10 text-zinc-500 hover:text-white"}`}>{p}</button>
                ))}
              </div>
              {platforms.includes("YouTube") && (
                <div className="mt-2">
                  <label className="block text-xs uppercase tracking-[0.15em] text-zinc-500 mb-1.5">YouTube privacy</label>
                  <select value={privacy} onChange={(e) => setPrivacy(e.target.value)} data-testid="batch-privacy" className="w-full bg-[#0A0A0A] border border-white/10 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 [color-scheme:dark]">
                    <option value="public">Public</option>
                    <option value="unlisted">Unlisted</option>
                    <option value="private">Private</option>
                  </select>
                  <p className="text-[11px] text-amber-400/80 mt-1.5">YouTube must be connected in Settings, or auto-posts will fail.</p>
                </div>
              )}
            </div>
          )}

          <div className="border border-white/10 rounded-sm bg-[#0A0A0A] px-4 py-3" data-testid="batch-cost-summary">
            <div className="flex items-center justify-between text-sm">
              <span className="text-zinc-400">{count} videos × {perVideo} credits</span>
              <span className="font-semibold text-white" data-testid="batch-cost-total">{total} credits</span>
            </div>
            <div className="flex items-center justify-between text-xs mt-1.5 text-zinc-500">
              <span>Your balance</span><span data-testid="batch-credit-balance">{credits} credits</span>
            </div>
            {!enough && (
              <div className="mt-3 flex items-start gap-2 rounded-sm border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-300" data-testid="batch-refill-warning">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>
                  Not enough credits for all {count} videos — you can fund about {affordable}. We'll schedule as many as your credits allow and pause the rest.{" "}
                  <a href="/credits" className="underline font-semibold hover:text-amber-200" data-testid="batch-refill-link">Refill credits</a> to complete the series.
                </span>
              </div>
            )}
          </div>

          <button onClick={start} disabled={saving || credits < perVideo} data-testid="batch-start-btn" className="w-full bg-sky-600 hover:bg-sky-500 py-2.5 rounded-md font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
            {saving ? "Starting…" : `Create & schedule ${count} video${count > 1 ? "s" : ""}`}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StreakMeter({ data }) {
  if (!data) return null;
  const s = data.current_streak || 0;
  const dow = (iso) => { try { return new Date(iso + "T00:00:00Z").toLocaleDateString("en", { weekday: "narrow", timeZone: "UTC" }); } catch { return "·"; } };
  return (
    <div className="rounded-md border border-amber-500/25 bg-gradient-to-r from-amber-500/10 via-orange-500/5 to-transparent p-5" data-testid="streak-meter">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`shrink-0 h-12 w-12 rounded-full grid place-items-center border ${s > 0 ? "border-amber-500/50 bg-amber-500/15" : "border-white/10 bg-white/5"}`}>
            <Flame className={`h-6 w-6 ${s > 0 ? "text-amber-400" : "text-zinc-600"}`} />
          </div>
          <div className="min-w-0">
            <div className="flex items-baseline gap-2">
              <span className="font-head text-2xl font-bold tracking-tight" data-testid="streak-count">{s}</span>
              <span className="text-sm text-zinc-400">day{s === 1 ? "" : "s"} streak</span>
              {data.best_streak > 0 && <span className="text-[11px] text-amber-300/70 ml-1">· best {data.best_streak}</span>}
            </div>
            <p className="text-xs text-zinc-300 mt-0.5">Consistent daily posting is the most consistent path to growth.</p>
            <p className="text-[11px] text-amber-300/80 mt-0.5">Videoileo can help you reach more people on social media and grow your influence.</p>
          </div>
        </div>
        <div className="sm:ml-auto flex items-end gap-1.5" data-testid="streak-last7">
          {(data.last7 || []).map((d) => (
            <div key={d.date} className="flex flex-col items-center gap-1">
              <span className={`h-6 w-6 rounded-md border grid place-items-center ${d.active ? "bg-amber-500/80 border-amber-400" : "bg-white/5 border-white/10"}`}>
                {d.active && <Flame className="h-3.5 w-3.5 text-black/70" />}
              </span>
              <span className="text-[10px] text-zinc-500 uppercase">{dow(d.date)}</span>
            </div>
          ))}
        </div>
      </div>
      {s === 0 && (
        <p className="text-xs text-amber-300/90 mt-3" data-testid="streak-nudge">
          {data.total_published > 0 ? "Your streak reset — publish something today to start a new one." : "Publish your first video today to start a streak."} Set a series on <strong>Every day</strong> to keep it alive automatically.
        </p>
      )}
    </div>
  );
}

function SeriesNarratorRow({ s, voices, onSaved }) {
  // Default narrator for every future episode of this series (manual, batch, autopilot).
  const narrators = voices.filter((v) => v.voice_id.startsWith("narrator:"));
  const openai = voices.filter((v) => v.voice_id.startsWith("openai:"));
  const value = s.voice_id?.startsWith("narrator:") || s.voice_id?.startsWith("openai:") ? s.voice_id : "openai:alloy";
  const isNarrator = value.startsWith("narrator:");

  const save = async (voice_id, overlay) => {
    try {
      const { data } = await api.put(`/series/${s.id}/voice`, { voice_id, narrator_overlay: overlay });
      toast.success(
        voice_id.startsWith("narrator:")
          ? `Every new episode will be narrated by ${narrators.find((v) => v.voice_id === voice_id)?.name || "this narrator"}${data.narrator_overlay ? " — on screen too" : ""}.`
          : "New episodes will use this voice."
      );
      onSaved();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="mt-3 flex items-center gap-2" data-testid={`series-narrator-row-${s.id}`}>
      {isNarrator ? (
        <img
          src={`${process.env.REACT_APP_BACKEND_URL}/api/narrators/${value.split(":")[1]}/avatar`}
          alt="Series narrator"
          className="h-9 w-9 rounded-full shrink-0 border border-white/10"
          data-testid={`series-narrator-avatar-${s.id}`}
        />
      ) : (
        <span className="h-9 w-9 shrink-0 grid place-items-center rounded-full border border-white/10 bg-white/5"><Mic className="h-4 w-4 text-zinc-500" /></span>
      )}
      <select
        value={value}
        onChange={(e) => save(e.target.value, s.narrator_overlay !== false)}
        data-testid={`series-narrator-select-${s.id}`}
        title="Default narrator — every new episode uses this voice"
        className="flex-1 min-w-0 bg-[#0A0A0A] border border-white/10 rounded-sm px-2.5 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 [color-scheme:dark]"
      >
        {narrators.length > 0 && <optgroup label="AI narrators">{narrators.map((v) => <option key={v.voice_id} value={v.voice_id}>{v.name}</option>)}</optgroup>}
        {openai.length > 0 && <optgroup label="Included voices">{openai.map((v) => <option key={v.voice_id} value={v.voice_id}>{v.name}</option>)}</optgroup>}
      </select>
      {isNarrator && (
        <button
          onClick={() => save(value, !s.narrator_overlay)}
          data-testid={`series-narrator-overlay-${s.id}`}
          title="Show the narrator's animated badge on screen in every episode"
          className={`shrink-0 px-2.5 h-9 rounded-sm border text-[11px] font-semibold transition-colors ${s.narrator_overlay ? "border-amber-500/40 bg-amber-500/15 text-amber-300" : "border-white/10 bg-[#0A0A0A] text-zinc-500 hover:text-white"}`}
        >
          On-screen {s.narrator_overlay ? "on" : "off"}
        </button>
      )}
    </div>
  );
}

function Tag({ children }) {
  return <span className="inline-flex items-center gap-1 bg-white/5 border border-white/10 rounded-full px-2 py-0.5 text-zinc-300">{children}</span>;
}

function NewSeriesDialog({ open, voices, onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", topic: "", niche: "Finance & Money", tone: "Energetic & engaging", cadence: "Weekly", platform: "YouTube", voice_id: "", narrator_overlay: true, generate_images: false, visual_style: "cinematic", music_track: "", music_volume: "subtle" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (open && voices.length && !form.voice_id) {
      setForm((f) => ({ ...f, voice_id: voices[0]?.voice_id || "" }));
    }
  }, [open, voices]); // eslint-disable-line

  const save = async () => {
    if (!form.name.trim() || !form.topic.trim()) { toast.error("Name and topic required"); return; }
    setSaving(true);
    try {
      await api.post("/series", form);
      toast.success("Series created — now hit Schedule to batch-produce episodes");
      onCreated();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#121212] border-white/10 text-white max-h-[88vh] overflow-y-auto" data-testid="series-dialog">
        <DialogHeader><DialogTitle className="font-head">New series</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Inp label="Series name" value={form.name} onChange={set("name")} testid="series-name" placeholder="Daily Money Tips" />
          <Inp label="Recurring topic theme" value={form.topic} onChange={set("topic")} testid="series-topic" placeholder="Personal finance habits for beginners" />
          <div className="grid grid-cols-2 gap-3">
            <Sel label="Niche" value={form.niche} onChange={set("niche")} options={NICHES} testid="series-niche" />
            <Sel label="Tone" value={form.tone} onChange={set("tone")} options={TONES} testid="series-tone" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Sel label="Cadence" value={form.cadence} onChange={set("cadence")} options={CADENCE} testid="series-cadence" />
            <Sel label="Platform" value={form.platform} onChange={set("platform")} options={PLATFORMS} testid="series-platform" />
          </div>
          <VoiceSelect voices={voices} value={form.voice_id} onChange={(v) => setForm((f) => ({ ...f, voice_id: v }))} testid="series-voice" />
          {form.voice_id?.startsWith("narrator:") && (
            <Toggle label="Show the narrator on screen in every episode" checked={form.narrator_overlay} onChange={(v) => setForm((f) => ({ ...f, narrator_overlay: v }))} testid="series-narrator-overlay" color="emerald" />
          )}
          <div>
            <label className="block text-xs uppercase tracking-[0.15em] text-zinc-500 mb-1.5">Background music <span className="normal-case tracking-normal text-zinc-600">· every episode</span></label>
            <MusicPicker
              track={form.music_track}
              volume={form.music_volume}
              testid="series-music"
              onChange={(v) => setForm((f) => ({ ...f, music_track: v.track || "", music_volume: v.volume }))}
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.15em] text-zinc-500 mb-1.5">Visual style</label>
            <StylePicker value={form.visual_style} onChange={(v) => setForm((f) => ({ ...f, visual_style: v }))} testid="series-visual-style" />
          </div>
          <Toggle label="Auto-generate scene visuals for episodes" checked={form.generate_images} onChange={(v) => setForm((f) => ({ ...f, generate_images: v }))} testid="series-genimages" />
          <button onClick={save} disabled={saving} data-testid="create-series-btn" className="w-full bg-blue-600 hover:bg-blue-500 py-2.5 rounded-md font-semibold transition-colors disabled:opacity-60">
            {saving ? "Creating…" : "Create series"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Inp({ label, value, onChange, testid, placeholder }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.15em] text-zinc-500 mb-1.5">{label}</label>
      <input value={value} onChange={onChange} data-testid={testid} placeholder={placeholder} className="w-full bg-[#0A0A0A] border border-white/10 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
    </div>
  );
}
function Sel({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.15em] text-zinc-500 mb-1.5">{label}</label>
      <select value={value} onChange={onChange} data-testid={testid} className="w-full bg-[#0A0A0A] border border-white/10 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
